import { RowData, TableMeta, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { css } from '@appd/uik';
import { MutableRefObject, useMemo, useRef } from 'react';
import { DataTableProps, getColumnDefs, getTableOptions, HeaderCell, TableCell } from '.';
import { useRowVirtualizer } from './hooks';

// use Tailwind for static styling, use inline style for dynamic styling
const STYLES = {
  CONTAINER: css.Styles({
    backgroundColor: 'inherit',
  }),
};

export const DataTable = <TData extends RowData>({ data, columnOverrides, tableOptions }: DataTableProps<TData>) => {
  const tableMeta = getTableOptions(tableOptions);
  const memoColumns = useMemo(
    () => (data && data.length > 0 ? getColumnDefs(data, columnOverrides, tableMeta) : []),
    [data, columnOverrides, tableMeta],
  );

  const table = useReactTable({
    data,
    columns: memoColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: tableMeta as TableMeta<TData>,
    // Uncomment if debugging is needed
    // debugAll: true,
  });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { rows } = table.getRowModel();
  const { virtualRows, paddingTop, paddingBottom } = useRowVirtualizer(
    table,
    tableContainerRef as MutableRefObject<HTMLDivElement>,
  );
  const tableRows = virtualRows?.map(virtualRow => rows[virtualRow.index]) ?? rows;

  if (!data || data.length === 0) {
    // Return EmptyBox component if no data (copied from EmptyBox.ts)
    // TODO: Convert EmptyBox.ts to React component
    return (
      <div className="flex justify-center items-center h-full rounded-md px-4 py-3 bg-inherit">
        <p className="text-center text-base text-default-text-medium">No data</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full relative overflow-hidden" style={STYLES.CONTAINER}>
      <div ref={tableContainerRef} className="flex w-full overflow-auto bg-inherit">
        <table
          className={`min-w-full text-sm bg-inherit border-spacing-0 ${
            table.options.meta?.enableStickyHeader ? 'relative border-separate' : 'border-collapse'
          }`}
        >
          <thead className="bg-inherit">
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className={`bg-inherit${table.options.meta?.enableStickyHeader ? ' sticky top-0 z-[1]' : ''}`}
              >
                {headerGroup.headers.map(header => {
                  return <HeaderCell key={header.id} {...header.getContext()} />;
                })}
              </tr>
            ))}
          </thead>
          <tbody className="overflow-auto bg-inherit">
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {tableRows.map(row => {
              return (
                <tr key={row.id} className="bg-inherit">
                  {row.getVisibleCells().map(cell => {
                    return <TableCell key={cell.id} {...cell.getContext()} />;
                  })}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
